import type { FlexGlCode, HighnoteTransactionEvent } from 'API';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectAllTransactions } from 'app/pages/store/transactionsSlice';
import { selectSearchAndFilter, setColumnFilters } from 'app/store/filtering/searchAndFilterSlice';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getPageName } from 'util/helpers';
import CodingEntityDropdown from 'app/pages/overview/tabs/transactions/CodingEntityDropdown';

interface FlexGlCodesDropdownProps<T> {
  selectedCode?: T | null;
  onValueChange: (option: T | null) => void;
  disabled?: boolean;
  disableHeader?: boolean;
  placeholder?: string | null;
  transaction?: HighnoteTransactionEvent;
}

const FlexGlCodesDropdown = <T extends FlexGlCode>({
  selectedCode,
  onValueChange,
  disabled,
  disableHeader = true,
  transaction,
}: FlexGlCodesDropdownProps<T>) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pageName = getPageName(location.pathname);
  const searchAndFilter = useAppSelector(selectSearchAndFilter);
  const currentFilters = searchAndFilter[pageName];
  const transactions = useAppSelector(selectAllTransactions);

  const updateFilterOptions = useCallback(
    (option: T | null) => {
      if (!transactions.length || !option) return;

      const usedGlCodes = new Map();

      for (const txn of transactions) {
        if (txn.flexGlCode?.id) {
          usedGlCodes.set(txn.flexGlCode.id, {
            id: txn.flexGlCode.id,
            label: txn.flexGlCode.name || 'Unknown GL Code',
            __typename: 'FilterOption',
          });
        }
      }

      usedGlCodes.set('NEEDS_CODING', {
        id: 'NEEDS_CODING',
        label: 'Unassigned',
        __typename: 'FilterOption',
      });

      const currentGlCodeFilter = currentFilters?.columnFilters?.flexGlCodeId;
      const currentOptions = currentGlCodeFilter?.options || [];
      const selectedValues = currentGlCodeFilter?.selectedValues || [];

      const allOptions = new Map(usedGlCodes);

      if (!allOptions.has(option.id)) {
        allOptions.set(option.id, {
          id: option.id,
          label: option.name || 'Unknown GL Code',
          __typename: 'FilterOption',
        });
      }

      selectedValues.forEach((selectedOption) => {
        const optionId = typeof selectedOption === 'string' ? selectedOption : selectedOption.id;
        const existingOption = currentOptions.find((opt) => opt.id === optionId);
        if (existingOption && !allOptions.has(optionId)) {
          allOptions.set(optionId, {
            id: existingOption.id,
            label: existingOption.label,
            __typename: 'FilterOption',
          });
        }
      });

      const finalOptions = Array.from(allOptions.values());

      dispatch(
        setColumnFilters({
          pageName,
          columnFilters: {
            flexGlCodeId: {
              key: 'flexGlCodeId',
              type: 'string',
              name: 'GL Code',
              page: pageName,
              options: finalOptions,
              selectedValues: selectedValues,
              active: currentGlCodeFilter?.active || false,
              rule: currentGlCodeFilter?.rule || 'noExcept',
            },
          },
        })
      );
    },
    [transactions, currentFilters, dispatch, pageName]
  );

  const handleValueChange = useCallback(
    (option: T | null) => {
      onValueChange(option);
      updateFilterOptions(option);
    },
    [onValueChange, updateFilterOptions]
  );

  return (
    <CodingEntityDropdown<T>
      erpType="flex"
      entityType="glCodes"
      selectedItem={selectedCode}
      onValueChange={handleValueChange}
      disabled={disabled}
      disableHeader={disableHeader}
      cardGroupId={transaction?.cardGroupId || undefined}
    />
  );
};

export default FlexGlCodesDropdown;
